import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  User, 
  Globe, 
  RotateCcw, 
  LogOut,
  CheckCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useNavigate } from 'react-router-dom';
import ProfileAvatar from './ProfileAvatar';

const Settings: React.FC = () => {
  const { memberstack, isAuthenticated, isLoading } = useAuth();
  const { t, language, setLanguage } = useI18n();
  const navigate = useNavigate();
  const [isResettingQuestionnaire, setIsResettingQuestionnaire] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<'male' | 'female' | 'neutral'>('neutral');

  // Load avatar preference from user data
  useEffect(() => {
    const loadAvatarPreference = async () => {
      if (memberstack) {
        try {
          const response = await memberstack.getMemberJSON();
          const userData = response.data || {};
          if (userData.avatarType && ['male', 'female', 'neutral'].includes(userData.avatarType)) {
            setSelectedAvatar(userData.avatarType);
          }
        } catch (error) {
          console.error('Failed to load avatar preference:', error);
        }
      }
    };
    loadAvatarPreference();
  }, [memberstack]);

  const handleAvatarChange = async (avatarType: 'male' | 'female' | 'neutral') => {
    setSelectedAvatar(avatarType);
    if (memberstack) {
      try {
        const response = await memberstack.getMemberJSON();
        const currentData = response.data || {};
        await memberstack.updateMemberJSON({
          json: {
            ...currentData,
            avatarType
          }
        });
      } catch (error) {
        console.error('Failed to save avatar preference:', error);
      }
    }
  };

  const handleReAnswerQuestions = async () => {
    if (!memberstack) return;
    
    setIsResettingQuestionnaire(true);
    try {
      // Clear questionnaire data from Memberstack
      const response = await memberstack.getMemberJSON();
      const currentData = response.data || {};
      
      await memberstack.updateMemberJSON({
        json: {
          ...currentData,
          questionnaire: null
        }
      });
      
      // Redirect to questionnaire route
      navigate('/questionnaire');
    } catch (error) {
      console.error('Failed to reset questionnaire:', error);
    } finally {
      setIsResettingQuestionnaire(false);
    }
  };

  const handleLogout = async () => {
    if (!memberstack) return;
    
    try {
      await memberstack.logout();
      // Redirect to onboarding
      navigate('/');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const languageNames = {
    de: 'Deutsch',
    en: 'English',
    fr: 'Français',
    it: 'Italiano'
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-border"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('nav.login')}</h1>
          <p className="text-gray-600 mb-6">{t('settings.loginRequired')}</p>
          <Button onClick={() => navigate('/')}>
            {t('nav.login')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-container">
        <div className="settings-header">
          <h1 className="settings-title flex items-center">
            {t('settings.title')}
          </h1>
          <p className="settings-subtitle">{t('settings.subtitle')}</p>
        </div>

        {/* Single Settings Card */}
        <div className="settings-card">
          <div className="settings-card-inner">
            <div className="settings-sections">
              {/* Account Section */}
              <div>
                <div className="settings-section-header">
                  <div className="settings-icon-box">
                    <User className="settings-icon" />
                  </div>
                  <h2 className="settings-section-title">{t('settings.account.title')}</h2>
                </div>
                
                <div className="settings-section-content">
                  <div className="settings-row">
                    <div>
                      <p className="font-light text-gray-900">{t('settings.account.status')}</p>
                      <p className="text-sm text-gray-600 font-light">{t('settings.account.loggedIn')}</p>
                    </div>
                    <Badge variant="outline" className="text-black border-black">
                      <CheckCircle className="icon-sm mr-1" />
                      {t('settings.account.active')}
                    </Badge>
                  </div>
                  
                  <div className="settings-divider"></div>
                  
                  <div className="settings-row">
                    <div>
                      <p className="font-light text-gray-900">{t('settings.account.logout')}</p>
                      <p className="text-sm text-gray-600 font-light">{t('settings.account.logoutDescription')}</p>
                    </div>
                    <Button 
                      variant="outline" 
                      onClick={handleLogout}
                      className="settings-button"
                    >
                      <LogOut className="icon-sm mr-2" />
                      {t('settings.account.logout')}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Preferences Section */}
              <div>
                <div className="settings-section-header">
                  <div className="settings-icon-box">
                    <Globe className="settings-icon" />
                  </div>
                  <h2 className="settings-section-title">{t('settings.preferences.title')}</h2>
                </div>
                
                <div className="settings-row">
                  <div>
                    <p className="font-light text-gray-900">{t('settings.preferences.language')}</p>
                    <p className="text-sm text-gray-600 font-light">{t('settings.preferences.currentLanguage').replace('{language}', languageNames[language])}</p>
                  </div>
                  <div className="language-buttons">
                    {Object.entries(languageNames).map(([code, name]) => (
                      <Button
                        key={code}
                        variant={language === code ? "default" : "outline"}
                        size="sm"
                        onClick={() => setLanguage(code as any)}
                        className={language === code ? 'language-button-active' : 'language-button'}
                      >
                        {name}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Avatar Section */}
              <div>
                <div className="settings-section-header">
                  <div className="settings-icon-box">
                    <User className="settings-icon" />
                  </div>
                  <h2 className="settings-section-title">{t('settings.avatar.title')}</h2>
                </div>
                
                <div className="settings-row">
                  <div>
                    <p className="font-light text-gray-900">{t('settings.avatar.title')}</p>
                    <p className="text-sm text-gray-600 font-light">{t('settings.avatar.current')}</p>
                  </div>
                  <div className="avatar-buttons">
                    {(['male', 'female', 'neutral'] as const).map((avatarType) => (
                      <button
                        key={avatarType}
                        onClick={() => handleAvatarChange(avatarType)}
                        className={`avatar-button ${selectedAvatar === avatarType ? 'avatar-button-active' : ''}`}
                      >
                        <ProfileAvatar avatarType={avatarType} size="md" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Budget Section */}
              <div>
                <div className="settings-section-header">
                  <div className="settings-icon-box">
                    <RotateCcw className="settings-icon" />
                  </div>
                  <h2 className="settings-section-title">{t('settings.budget.title')}</h2>
                </div>
                
                <div className="settings-section-content">
                  <div className="settings-row">
                    <div>
                      <p className="font-light text-gray-900">{t('settings.budget.reAnswerQuestions')}</p>
                      <p className="text-sm text-gray-600 font-light">
                        {t('settings.budget.reAnswerDescription')}
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      onClick={handleReAnswerQuestions}
                      disabled={isResettingQuestionnaire}
                      className="settings-button"
                    >
                      {isResettingQuestionnaire ? (
                        <>
                          <div className="spinner-border-sm"></div>
                          {t('settings.budget.resetting')}
                        </>
                      ) : (
                        <>
                          <RotateCcw className="icon-sm mr-2" />
                          {t('settings.budget.reAnswerButton')}
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <div className="settings-note-box">
                    <p className="text-sm text-black font-light">
                      <strong>{t('settings.budget.note')}</strong> {t('settings.budget.noteText')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;